import { DestroyableType } from 'api-hooks/general/model';
import { useDestroy } from 'api-hooks/general/mutation';
import notification from 'components/common/notification';
import { PopoverMenuType } from 'components/popover';
import useTranslation from 'next-translate/useTranslation';

import useDialog from './use-dialog';
import useDismiss from './use-dismiss';
import useGetCrudPermission from './use-get-crud-permission';
import useNavigation from './use-navigation';

export default function useDestroyAction(type: DestroyableType, id?: string) {
  const { t } = useTranslation();
  const dialog = useDialog();
  const deleteMutate = useDestroy();
  const onDismiss = useDismiss(type);
  const { close } = useNavigation();

  const permission = useGetCrudPermission(type);

  const deletePermission = permission.delete;

  const onDelete = (currentId?: string) => {
    const _id = currentId ?? id;

    const message = {
      type,
      id: _id || 'undefined',
      message: 'Invalid Destroy Action (FE)',
    };

    if (!_id) {
      notification.error({
        message: JSON.stringify(message),
      });
      return;
    }

    dialog.showConfirmation({
      message: t('common:confirmation_delete_text'),
      title: t('common:confirmation'),
      onPositiveAction: async (dismiss) => {
        try {
          await deleteMutate.mutateAsync({
            id: _id,
            type,
          });
          close();
          onDismiss(_id);
          notification.success({ message: t('common:successfully_deleted') });
        } catch (error) {
          notification.error({ message: error.message });
        } finally {
          dismiss();
        }
      },
      onNegativeAction: (dismiss) => {
        dismiss();
      },
    });
  };

  const deleteAction: PopoverMenuType = {
    key: `${type}-${id}`,
    label: t('common:delete'),
    onClick: onDelete,
    isAllowed: deletePermission,
    isError: true,
  };

  return {
    onDelete,
    permission,
    deletePermission,
    deleteAction,
    deleteMutate,
    ...deleteMutate,
  };
}
